import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Scale, Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { API_BASE } from "../lib/apiBase";
import { useAuth } from "../contexts/AuthContext";

const CHAT_PATH = "/chat";

const PaymentConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const planCode = searchParams.get("plan_code") || "";
  const [phase, setPhase] = useState("verifying"); // verifying | active | processing | failed
  const [errorMsg, setErrorMsg] = useState("");
  const [planInfo, setPlanInfo] = useState(null);
  const pollRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const finalize = async () => {
      try {
        const { data } = await axios.post(
          `${API_BASE}/api/payments/finalize`,
          { plan_code: planCode || undefined },
          { withCredentials: true }
        );
        if (cancelled) return;
        setPlanInfo(data);
        if (data.status === "active") {
          if (typeof refreshUser === "function") await refreshUser();
          setPhase("active");
        } else if (data.status === "processing" || data.status === "awaiting_charge") {
          setPhase("processing");
          startPolling();
        } else if (data.status === "not_registered") {
          setErrorMsg("La tarjeta no fue registrada. Reintenta el checkout.");
          setPhase("failed");
        } else {
          setPhase("processing");
          startPolling();
        }
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setErrorMsg(typeof detail === "string" ? detail : "No pudimos confirmar tu pago. Si Flow te cobró, escríbenos.");
        setPhase("failed");
      }
    };

    const startPolling = () => {
      const tick = async () => {
        if (cancelled) return;
        pollRef.current += 1;
        try {
          const { data } = await axios.get(`${API_BASE}/api/payments/subscription`, {
            withCredentials: true,
          });
          const sub = data?.subscription;
          if (sub && sub.status === "active") {
            if (typeof refreshUser === "function") await refreshUser();
            setPhase("active");
            setPlanInfo({ plan_code: sub.plan_code, status: "active" });
            return;
          }
        } catch (_) {
          /* ignore transient errors during polling */
        }
        if (pollRef.current >= 20) {
          setPhase("processing"); // give up polling but keep encouraging message
          return;
        }
        setTimeout(tick, 3000);
      };
      tick();
    };

    finalize();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="payment-confirmation-root"
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{
        background: "transparent",
        color: "var(--pl-text)",
      }}
    >
      <div
        className="w-full max-w-lg rounded-2xl p-8 sm:p-10"
        style={{
          background: "var(--pl-card)",
          border: "1px solid var(--pl-border)",
          boxShadow: "var(--pl-shadow-lg)",
        }}
      >
        <div className="flex items-center gap-3 mb-6">
          <span
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              color: "#d4af37",
            }}
          >
            <Scale className="w-5 h-5" />
          </span>
          <h1 className="text-2xl font-bold" style={{ color: "var(--pl-text-strong)" }}>
            Pre-Legal<span style={{ color: "var(--pl-primary)" }}>.cl</span>
          </h1>
        </div>

        {phase === "verifying" && (
          <div className="text-center" data-testid="payment-state-verifying">
            <Loader2 className="w-12 h-12 mx-auto animate-spin" style={{ color: "var(--pl-primary)" }} />
            <h2 className="text-xl mt-4 mb-2" style={{ color: "var(--pl-text-strong)" }}>Confirmando tu pago…</h2>
            <p className="text-sm" style={{ color: "var(--pl-text-muted)" }}>Verificando con Flow.cl que tu tarjeta quedó registrada.</p>
          </div>
        )}

        {phase === "processing" && (
          <div className="text-center" data-testid="payment-state-processing">
            <Loader2 className="w-12 h-12 mx-auto animate-spin" style={{ color: "var(--pl-primary)" }} />
            <h2 className="text-xl mt-4 mb-2" style={{ color: "var(--pl-text-strong)" }}>Procesando primer cobro…</h2>
            <p className="text-sm leading-relaxed" style={{ color: "var(--pl-text-muted)" }}>
              Tu tarjeta quedó registrada y la suscripción fue creada. Flow.cl está
              ejecutando el primer cobro — recibirás tus consultas en cuanto se
              confirme (suele tardar pocos segundos).
            </p>
            <p className="text-xs mt-3" style={{ color: "var(--pl-text-subtle)" }}>
              Puedes cerrar esta página; tus consultas se cargarán automáticamente.
            </p>
            <div className="mt-6">
              <Link
                to={CHAT_PATH}
                data-testid="payment-go-chat-processing"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: "var(--pl-primary-soft)", color: "var(--pl-primary)", border: "1px solid rgba(37,99,235,0.30)" }}
              >
                Ir al chat <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {phase === "active" && (
          <div className="text-center" data-testid="payment-state-active">
            <CheckCircle2 className="w-14 h-14 mx-auto" style={{ color: "var(--pl-success)" }} />
            <h2 className="text-2xl mt-4 mb-2" style={{ color: "var(--pl-text-strong)" }}>¡Suscripción activa!</h2>
            <p className="text-sm leading-relaxed" style={{ color: "var(--pl-text-muted)" }}>
              {planInfo?.consultas
                ? `Acabas de recibir ${planInfo.consultas === 999999 ? "consultas ilimitadas" : `${planInfo.consultas} consultas`} en tu cuenta.`
                : "Tus consultas ya están disponibles en tu cuenta."}
            </p>
            <div className="mt-6">
              <Link
                to={CHAT_PATH}
                data-testid="payment-go-chat-active"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold"
                style={{ background: "var(--pl-primary)", color: "#fff" }}
              >
                Empezar a consultar <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {phase === "failed" && (
          <div className="text-center" data-testid="payment-state-failed">
            <AlertCircle className="w-12 h-12 mx-auto" style={{ color: "var(--pl-error)" }} />
            <h2 className="text-xl mt-4 mb-2" style={{ color: "var(--pl-text-strong)" }}>No pudimos confirmar el pago</h2>
            <p className="text-sm leading-relaxed" style={{ color: "var(--pl-text-muted)" }}>
              {errorMsg || "Si tu tarjeta fue cobrada, escríbenos a soporte@acbothub.com con el comprobante de Flow."}
            </p>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <button
                onClick={() => navigate("/")}
                data-testid="payment-back-home"
                className="px-5 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: "var(--pl-card-soft)", color: "var(--pl-text)", border: "1px solid var(--pl-border)" }}
              >
                Volver al inicio
              </button>
              <Link
                to={CHAT_PATH}
                data-testid="payment-go-chat-failed"
                className="px-5 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: "var(--pl-primary)", color: "#fff" }}
              >
                Ir al chat
              </Link>
            </div>
          </div>
        )}

        <div className="mt-8 text-xs text-center" style={{ color: "var(--pl-text-subtle)" }}>
          {user?.email ? `Sesión: ${user.email}` : null}
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmationPage;
